import React from 'react';
import { OverlayDisplayDiv } from '@UI-Elements/PopoverOverlays/OverlayDisplayDiv';
import { Close } from '@mui/icons-material';
import { useAccountStore } from '@components/Stores/accountStore';
import { getCurrencySymbol } from '../../../hooks/useCurrencyRates';
import { useFx } from '../../../hooks/useFx';
import { useBaseCurrency } from '../../../hooks/useBaseCurrency';
import { formatToPriceInDollars } from '../../../utils/FormatValue';
import { IHistoryPrice, IFormulaToAccount } from '../../../sp-types';
import { getCurveForIndex, PricePoint } from './HistoricalUtils';
import { CurveMiniChart } from './CurveMiniChart';

export const TodayPricePopup: React.FC<{
  onClose: () => void;
  material: IFormulaToAccount;
  historyPrices: IHistoryPrice[];
  quoteUnit: string | null;
  isRatio?: boolean;
}> = ({ onClose, material, historyPrices, quoteUnit, isRatio }) => {
  const { formulaColorDictionary: colorsMap } = useAccountStore();
  const baseCurrency = useBaseCurrency();
  const { convertToBase } = useFx();
  
  const nativePrice = Number(material.currentPrice ?? 0);
  const nativeCurrency = material.currency || baseCurrency;
  
  // Convert to base currency
  const { amountBase: basePriceRaw, rate: fxRate } = convertToBase(nativePrice, nativeCurrency);
  const basePrice = basePriceRaw ?? 0;
  const isConverted = nativeCurrency !== baseCurrency && fxRate !== null;
  const latestEntry = historyPrices && historyPrices.length > 0 ? historyPrices[historyPrices.length - 1] : undefined;
  const latestDateLabel = latestEntry
    ? new Date(latestEntry.date).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    : undefined;

  const baseCode = material.formulaId;
  const quoteSourceSymbol = material.quoteSourceSymbol || undefined;
  const showSymbolBadge = quoteSourceSymbol && quoteSourceSymbol !== baseCode;
  const headerBaseColor = colorsMap[baseCode] || '#e5e7eb';
  const headerBgColor = hexToRgba(headerBaseColor, 0.15);

  return (
    <OverlayDisplayDiv hideClose setDisplayOverlay={onClose}>
      <div className="flex flex-col bg-white rounded-lg shadow-xl max-w-4xl w-full">
        {/* Header — single strip, uniform sizing, vertically centered */}
        <div
          className="flex items-center px-5 py-3 border-b border-gray-200 gap-6"
          style={{ backgroundColor: headerBgColor }}
        >
          {/* Material name + badge */}
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-sm font-bold text-gray-900">
              {material.displayName || baseCode}
            </span>
            {showSymbolBadge && (
              <span className="px-1.5 py-0.5 rounded bg-white/60 text-xs text-gray-500 border border-gray-200">
                {quoteSourceSymbol}
              </span>
            )}
          </div>

          {/* Spot price block — all items-center, one consistent size */}
          <div className="flex items-center gap-1.5 flex-1 justify-center">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Spot</span>
            <span className="text-lg font-bold text-gray-900">
              {!isRatio && getCurrencySymbol(nativeCurrency)} {nativePrice.toLocaleString('en-US', { maximumFractionDigits: isRatio ? 4 : 0 })}
            </span>
            {quoteUnit && (
              <span className="text-xs text-gray-500">/ {quoteUnit}</span>
            )}
            {!isRatio && nativeCurrency !== baseCurrency && isConverted && (
              <>
                <span className="text-gray-300 mx-1">|</span>
                <span className="text-xs text-gray-500">
                  ≈ {getCurrencySymbol(baseCurrency)} {basePrice.toLocaleString('en-US', { maximumFractionDigits: 0 })} {baseCurrency}
                </span>
              </>
            )}
            {latestDateLabel && (
              <>
                <span className="text-gray-300 mx-1">|</span>
                <span className="text-xs text-gray-400">{latestDateLabel}</span>
              </>
            )}
          </div>

          {/* Close */}
          <button 
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors shrink-0"
            aria-label="Close"
          >
            <Close className="text-gray-400 hover:text-gray-600" sx={{ fontSize: 20 }} />
          </button>
        </div>

        {/* Content */}
        <div className="px-5 py-4">
          <PriceCard
            material={material}
            basePrice={basePrice}
            nativePrice={nativePrice}
            nativeCurrency={nativeCurrency}
            baseCurrency={baseCurrency}
            historyPrices={historyPrices}
            isRatio={isRatio}
          />
        </div>
      </div>
    </OverlayDisplayDiv>
  );
};

interface PriceCardProps {
  material: IFormulaToAccount;
  basePrice: number;
  nativePrice: number;
  nativeCurrency: string;
  baseCurrency: string;
  historyPrices: IHistoryPrice[];
  isRatio?: boolean;
}

const PriceCard: React.FC<PriceCardProps> = ({ material, basePrice, nativePrice, nativeCurrency, baseCurrency, historyPrices, isRatio }) => {
  const [futurePrices, setFuturePrices] = React.useState<PricePoint[]>([]);

  React.useEffect(() => {
    if (!historyPrices || historyPrices.length === 0) {
      setFuturePrices([]);
      return;
    }

    const curveData = getCurveForIndex(historyPrices.length - 1, new Date(), historyPrices);
    setFuturePrices(curveData);
  }, [historyPrices]);

  return (
    <div className="flex flex-col gap-4">
      {/* Currency conversion boxes — hide for ratio materials */}
      {!isRatio && (
        <div>
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Spot price in other currencies <span className="normal-case tracking-normal font-normal text-gray-400">— based on recent FX rates</span></h3>
          <PricesRows 
            basePrice={basePrice}
            nativePrice={nativePrice}
            nativeCurrency={nativeCurrency}
            baseCurrency={baseCurrency}
          />
        </div>
      )}
      
      {/* Futures curve chart */}
      <div>
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Futures Curve</h3>
        <div className="bg-gray-50 rounded-lg p-3">
          <div className="w-full h-64">
            <CurveMiniChart
              routePrices={futurePrices}
              cssClass="w-full h-full"
              currency={isRatio ? '' : getCurrencySymbol(material.currency || '')}
              showTicks
            />
          </div>
        </div>
      </div>
    </div>
  );
};

interface PricesRowsProps {
  basePrice: number;
  nativePrice: number;
  nativeCurrency: string;
  baseCurrency: string;
}
export const PricesRows: React.FC<PricesRowsProps> = ({ basePrice, nativePrice, nativeCurrency, baseCurrency }) => {
  const { convertToBase, rates } = useFx();
  
  // Build dynamic currency list: base + all available FX rates
  const availableCurrencies = React.useMemo(() => {
    const currencies = new Set<string>([baseCurrency]);
    
    // Add all currencies we have rates for
    if (rates) {
      Object.keys(rates).forEach(curr => currencies.add(curr));
    }
    
    // Add native currency if different
    if (nativeCurrency !== baseCurrency) {
      currencies.add(nativeCurrency);
    }
    
    // Convert to sorted array, with base currency first
    const sorted = Array.from(currencies).sort();
    const baseIndex = sorted.indexOf(baseCurrency);
    if (baseIndex > 0) {
      sorted.splice(baseIndex, 1);
      sorted.unshift(baseCurrency);
    }
    
    return sorted;
  }, [baseCurrency, nativeCurrency, rates]);
  
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
      {availableCurrencies.map((currency) => {
        const symbol = getCurrencySymbol(currency);
        let displayPrice: number;
        let isNative = false;
        
        if (currency === baseCurrency) {
          // Base currency - show converted base price
          displayPrice = basePrice;
        } else if (currency === nativeCurrency) {
          // Native currency - show original price
          displayPrice = nativePrice;
          isNative = true;
        } else {
          // Other currency - convert from base to target currency
          // rates[currency] gives us: 1 BASE = X CURRENCY
          // So: basePrice * rate = price in target currency
          const targetRate = rates?.[currency];
          if (targetRate) {
            displayPrice = basePrice * targetRate;
          } else {
            // No rate available
              return (
                <div
                  key={currency}
                  className="bg-gray-50 border border-gray-200 px-4 py-3 rounded-lg flex flex-col items-center opacity-50"
                >
                  <span className="text-xs font-semibold text-gray-500 tracking-wide mb-1">{currency}</span>
                  <span className="text-xs text-gray-400 font-medium">No rate</span>
                </div>
              );
          }
        }
        
        return (
          <div
            key={currency}
            className={`px-4 py-3 rounded-lg flex flex-col items-center transition-all ${
              currency === baseCurrency 
                ? 'bg-blue-50 border-2 border-blue-200 shadow-sm' 
                : isNative
                  ? 'bg-white border-2 border-green-350 shadow-sm'
                  : 'bg-white border border-gray-200 shadow-sm hover:shadow-md'
            }`}
          >
            <div className="flex items-center gap-1 mb-1">
              <span className={`text-xs font-semibold tracking-wide ${
                currency === baseCurrency ? 'text-blue-700' : 'text-gray-600'
              }`}>
                {currency}
              </span>
              {isNative && (
                <span className="text-[9px] text-gray-500 font-medium">(native)</span>
              )}
            </div>
            <div className="flex items-baseline gap-1">
              <span className="text-xs text-gray-500">{symbol}</span>
              <span className={`text-base font-bold ${
                currency === baseCurrency ? 'text-blue-900' : 'text-gray-900'
              }`}>
                {displayPrice.toLocaleString('en-US', { 
                  minimumFractionDigits: 0,
                  maximumFractionDigits: currency === baseCurrency ? 2 : 0
                })}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Lightweight utility to convert a hex color (#RGB or #RRGGBB) into an rgba() string
function hexToRgba(hex: string, alpha: number): string {
  let cleaned = hex.replace('#', '');

  if (cleaned.length === 3) {
    cleaned = cleaned
      .split('')
      .map((c) => c + c)
      .join('');
  }

  const r = parseInt(cleaned.substring(0, 2), 16) || 0;
  const g = parseInt(cleaned.substring(2, 4), 16) || 0;
  const b = parseInt(cleaned.substring(4, 6), 16) || 0;

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
